import {
  collection, doc, addDoc, getDoc, getDocs, setDoc, updateDoc, runTransaction,
  query, where, orderBy, limit, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { hoyISO } from '../utils/finance';

// ─── CAJAS ────────────────────────────────────────────────────────────────
export async function getCajas(branchId) {
  const base = collection(db, 'cajas');
  const q = branchId
    ? query(base, where('branchId', '==', branchId))
    : base;
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.activa !== false);
}

export async function crearCaja({ nombre, branchId, branchName }) {
  const ref = await addDoc(collection(db, 'cajas'), {
    nombre, branchId: branchId || '', branchName: branchName || '',
    activa: true, createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── TURNOS ───────────────────────────────────────────────────────────────
// Devuelve el turno abierto de una caja (o de la sucursal) para hoy, o null.
export async function getTurnoActivo({ cajaId, branchId }) {
  const base = collection(db, 'turnosCaja');
  const filters = [where('estado', '==', 'abierto'), where('fechaTurno', '==', hoyISO())];
  if (cajaId) filters.push(where('cajaId', '==', cajaId));
  else if (branchId) filters.push(where('branchId', '==', branchId));
  const snap = await getDocs(query(base, ...filters));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function abrirTurno({ caja, montoApertura, usuario }) {
  const data = {
    cajaId: caja.id,
    cajaNombre: caja.nombre || '',
    branchId: caja.branchId || '',
    branchName: caja.branchName || '',
    usuarioAperturaId: usuario.uid || '',
    usuarioAperturaNombre: usuario.name || usuario.email || '',
    usuarioCierreId: null,
    usuarioCierreNombre: null,
    fechaTurno: hoyISO(),
    aperturaAt: serverTimestamp(),
    cierreAt: null,
    montoApertura: Math.round(Number(montoApertura) || 0),
    totalEfectivo: 0, totalDebito: 0, totalCredito: 0, totalTransferencia: 0, totalOtros: 0,
    totalBruto: 0, totalDescuentos: 0, totalNeto: 0, totalIVA: 0, totalExento: 0,
    conteoVentas: 0,
    cuadreEfectivo: null, diferencia: null, observacionesCierre: '',
    estado: 'abierto',
  };
  const ref = await addDoc(collection(db, 'turnosCaja'), data);
  return { id: ref.id, ...data };
}

// ─── CORRELATIVOS (folios de boleta y N° de presupuesto) ────────────────────
// Usa una transacción sobre counters/{key} para garantizar unicidad.
export async function siguienteCorrelativo(key, { prefix = '', pad = 0, includeYear = false } = {}) {
  const ref = doc(db, 'counters', key);
  const numero = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const actual = snap.exists() ? (snap.data().value || 0) : 0;
    const next = actual + 1;
    if (snap.exists()) tx.update(ref, { value: next, updatedAt: serverTimestamp() });
    else tx.set(ref, { value: next, createdAt: serverTimestamp() });
    return next;
  });
  const year = includeYear ? `${new Date().getFullYear()}-` : '';
  const num = pad > 0 ? String(numero).padStart(pad, '0') : String(numero);
  return `${prefix}${year}${num}`;
}

export const siguienteFolioBoleta = () => siguienteCorrelativo('folioBoleta', { pad: 6 });
export const siguienteNumeroPresupuesto = () =>
  siguienteCorrelativo('presupuesto', { prefix: 'PRES-', includeYear: true, pad: 3 });

// ─── CIERRE DE TURNO ────────────────────────────────────────────────────────
export async function cerrarTurno(turnoId, payload) {
  await updateDoc(doc(db, 'turnosCaja', turnoId), {
    ...payload,
    cierreAt: serverTimestamp(),
  });
}

export { Timestamp, getDoc, setDoc };
